/**
 * AgentCard — Settings → Flint AI ▸ Agent.
 *
 * Configure the assistant's behaviour. Flint AI on/off is no longer here — it's
 * the AI *add-on* (migration 0107), the single gate that also makes this whole
 * page appear; this card is only reachable when the add-on is on. So it just
 * carries:
 *   - Workspace default language (companies.ai_default_language). One global
 *     choice now — not per-channel.
 *   - Default mode (Clarity/Operator) — V1.x, shown disabled-with-reason.
 *
 * Controlled: the route owns {aiEnabled, aiDefaultLanguage} state + the SaveBar.
 * (`aiEnabled` is retained in the value shape for the save contract but is no
 * longer toggled here.)
 */
import { Sparkles, Sparkle, ArrowRight } from "lucide-react";
import { Link } from "@tanstack/react-router";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StatusPill,
} from "@flint/ui";
import { FormCard } from "../form-card.tsx";

/**
 * Workspace language options. `"auto"` is the dropdown sentinel for
 * auto-detect — Radix `Select` reserves the empty string, so we can't use ""
 * as an item value. State/persistence still use "" (→ null) for auto-detect;
 * the card maps between the two.
 */
export const AUTO_LANGUAGE = "auto";
export const AI_LANGUAGE_OPTIONS = [
  { value: AUTO_LANGUAGE, label: "Auto-detect" },
  { value: "en", label: "English" },
  { value: "hi", label: "हिन्दी (Hindi)" },
  { value: "ta", label: "தமிழ் (Tamil)" },
  { value: "ml", label: "മലയാളം (Malayalam)" },
  { value: "kn", label: "ಕನ್ನಡ (Kannada)" },
] as const;

export interface AgentCardValues {
  aiEnabled: boolean;
  /** `""` = auto-detect; otherwise an ISO 639-1 code. */
  aiDefaultLanguage: string;
}

export interface AgentCardProps {
  values: AgentCardValues;
  onChange: (next: AgentCardValues) => void;
}

export function AgentCard({ values, onChange }: AgentCardProps) {
  return (
    <FormCard
      id="agent"
      icon={<Sparkles size={14} strokeWidth={2} aria-hidden />}
      title="Agent"
      description="Set the language Flint AI speaks. Tax calculation, e-invoicing and audit logging are not AI and keep running regardless."
      badge={
        <StatusPill tone="ok" size="xs">
          On
        </StatusPill>
      }
    >
      {/* Availability is the add-on (migration 0107) — managed from Subscription. */}
      <Link
        to={"/settings/subscription" as never}
        className="flex items-center justify-between gap-4 rounded-md border border-line bg-raised p-3 transition-colors hover:bg-bg"
      >
        <div className="min-w-0">
          <p className="text-sm font-semibold text-ink">Flint AI is on for this workspace</p>
          <p className="mt-0.5 text-xs leading-relaxed text-muted">
            Availability is an add-on — turn Flint AI on or off from Subscription → Add-ons.
          </p>
        </div>
        <ArrowRight size={16} className="shrink-0 text-muted" aria-hidden />
      </Link>

      {/* Default language */}
      <div className="mt-3 flex flex-col gap-1.5">
        <label htmlFor="ai-default-language" className="text-sm font-medium text-ink">
          Default language
        </label>
        <Select
          value={values.aiDefaultLanguage || AUTO_LANGUAGE}
          onValueChange={(v) =>
            onChange({ ...values, aiDefaultLanguage: v === AUTO_LANGUAGE ? "" : v })
          }
        >
          <SelectTrigger
            id="ai-default-language"
            className="h-9 max-w-xs"
            aria-label="Flint AI default language"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {AI_LANGUAGE_OPTIONS.map((o) => (
              <SelectItem key={o.value || "auto"} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs leading-relaxed text-muted">
          Used everywhere the assistant works — voice transcription and replies across every channel.
          Auto-detect picks the language from each message.
        </p>
      </div>

      {/* Default mode — V1.x, disabled-with-reason */}
      <div className="mt-3 flex items-center justify-between gap-4 rounded-md border border-dashed border-line bg-bg/40 p-3 opacity-70">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Sparkle size={13} className="text-muted" aria-hidden />
            <p className="text-sm font-medium text-ink-2">Default mode</p>
            <StatusPill tone="muted" size="xs">
              Coming soon
            </StatusPill>
          </div>
          <p className="mt-0.5 text-xs leading-relaxed text-muted">
            Choose how Flint replies by default — Clarity (founder briefings, plain language) or
            Operator (terse, fast). Lands in a later release.
          </p>
        </div>
        <div className="flex shrink-0 overflow-hidden rounded-md border border-line text-xs">
          <span className="bg-paper px-2.5 py-1 font-medium text-muted">Clarity</span>
          <span className="border-l border-line px-2.5 py-1 text-muted">Operator</span>
        </div>
      </div>
    </FormCard>
  );
}
